import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Target, Eye, HeartHandshake, Sparkles, CheckCircle2, ArrowRight, Building2 } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { useClinic } from "../context/ClinicContext";
import { ScrollReveal } from "../components/ScrollReveal";
import { SectionHeading } from "../components/SectionHeading";
import { AnimatedCounter } from "../components/AnimatedCounter";

const PageHero = ({ eyebrow, title, subtitle }) => (
  <section className="gradient-bg-soft grid-pattern border-b border-slate-100">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 lg:py-20 text-center">
      <ScrollReveal>
        <p className="eyebrow mb-3">{eyebrow}</p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0F1E36]">{title}</h1>
        {subtitle && <p className="mt-4 max-w-2xl mx-auto text-[#627D98] leading-relaxed">{subtitle}</p>}
      </ScrollReveal>
    </div>
  </section>
);

export { PageHero };

export default function About() {
  const c = useClinic();
  const { data: doctors = [] } = useQuery({ queryKey: ["doctors"], queryFn: async () => (await api.get("/doctors")).data });
  const doc = doctors[0];

  return (
    <div>
      <PageHero eyebrow="About Us" title="About Revive Family Clinic" subtitle="Compassionate family healthcare and advanced diagnostics, all under one trusted roof." />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <ScrollReveal>
            <div className="relative rounded-[2rem] overflow-hidden shadow-xl h-[400px] bg-[#0B1B33] flex items-center justify-center">
              <div className="absolute inset-0 grid-pattern opacity-30" />
              <div className="absolute -top-16 -right-10 w-56 h-56 rounded-full bg-[#2BB673]/25 blur-3xl" />
              <div className="absolute -bottom-16 -left-10 w-56 h-56 rounded-full bg-[#1A75BC]/30 blur-3xl" />
              <div className="relative bg-white rounded-3xl p-8 shadow-2xl">
                <img src="/revive-logo.png" alt="Revive Family Clinic & X-Ray Diagnostics" className="w-64 object-contain" />
              </div>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.12}>
            <div className="inline-flex items-center gap-2 text-xs font-bold bg-[#E8F4FD] text-[#1A75BC] px-3 py-1.5 rounded-full mb-4">
              <Building2 size={14} /> Established {c.established || "2015"}
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0F1E36] mb-4">Our Story</h2>
            <p className="text-[#627D98] leading-relaxed">{c.story}</p>
            <p className="mt-4 text-[#627D98] leading-relaxed">{c.philosophy}</p>
          </ScrollReveal>
        </div>
      </section>

      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-6">
          {[["Our Mission", c.mission, Target], ["Our Vision", c.vision, Eye]].map(([t, d, Icon], i) => (
            <ScrollReveal key={t} delay={i * 0.1}>
              <div className="card-lift bg-white rounded-2xl border border-slate-100 p-8 h-full">
                <div className="w-14 h-14 rounded-2xl gradient-bg flex items-center justify-center text-white mb-5"><Icon size={26} /></div>
                <h3 className="text-xl font-bold text-[#0F1E36] mb-3">{t}</h3>
                <p className="text-[#627D98] leading-relaxed">{d}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeading eyebrow="Why Revive" title="Why Patients Choose Us" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[["Family-first Care", "Personalised healthcare for every member of your family."], ["Modern Facilities", "Clean, hygienic, patient-friendly clinical spaces."], ["X-Ray Service", "Digital X-Ray services are coming soon."], ["Experienced Doctor", "General Physician and Family Doctor with MBBS, MD and Diploma in Diabetology."]].map(([t, d], i) => (
              <ScrollReveal key={t} delay={i * 0.06}>
                <div className="bg-[#F0F7FB] rounded-2xl p-6 h-full">
                  <HeartHandshake size={26} className="text-[#27AE60] mb-4" />
                  <h4 className="font-bold text-[#0F1E36] mb-2">{t}</h4>
                  <p className="text-sm text-[#627D98] leading-relaxed">{d}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {(c.stats || []).length > 0 && (
        <section className="pb-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {c.stats.map((s, i) => (
              <ScrollReveal key={i} delay={i * 0.06}>
                <div className="text-center gradient-bg-soft rounded-2xl p-6">
                  <div className="text-3xl font-extrabold gradient-text font-mono"><AnimatedCounter value={s.value} suffix={s.suffix} /></div>
                  <div className="mt-1 text-xs font-semibold text-[#627D98]">{s.label}</div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </section>
      )}

      {doc && (
        <section className="py-16 lg:py-24 bg-[#F0F7FB]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div className="relative">
                <div className="absolute -inset-3 gradient-bg rounded-[2rem] rotate-2 opacity-90" />
                <img src={fileUrl(doc.image)} alt={doc.name} className="relative rounded-[2rem] w-full max-w-md mx-auto object-cover shadow-xl" />
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.12}>
              <p className="eyebrow mb-3 flex items-center gap-2"><Sparkles size={14} /> Meet Our Doctor</p>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0F1E36]">{doc.name}</h2>
              <p className="mt-2 text-[#1A75BC] font-semibold">{doc.qualification}</p>
              <p className="mt-1 text-sm text-[#627D98]">{doc.specialization} · {doc.experience}</p>
              <p className="mt-5 text-[#627D98] leading-relaxed">{doc.description}</p>
              {doc.expertise?.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-bold text-[#0F1E36] mb-3">Areas of Expertise</h4>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {doc.expertise.map((e) => (
                      <div key={e} className="flex items-start gap-2 text-sm text-[#334E68]"><CheckCircle2 size={16} className="text-[#27AE60] shrink-0 mt-0.5" /> {e}</div>
                    ))}
                  </div>
                </div>
              )}
              {doc.languages && <p className="mt-5 text-sm text-[#627D98]"><span className="font-bold text-[#0F1E36]">Languages Known:</span> {doc.languages}</p>}
            </ScrollReveal>
          </div>
        </section>
      )}
    </div>
  );
}
